import { useState, useRef } from 'react';

interface ImageCropperProps {
  imageSrc: string;
  onCrop: (croppedFile: File) => void;
  onCancel: () => void;
  fileName: string;
}

export function ImageCropper({ imageSrc, onCrop, onCancel, fileName }: ImageCropperProps) {
  const C = 280; // Crop box container size (square)
  const targetSize = 400; // Target output resolution (400x400)

  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [baseSize, setBaseSize] = useState({ width: 0, height: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);

  const imgRef = useRef<HTMLImageElement>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);

  // Compute base dimensions when image is loaded
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    setNaturalSize({ width: nw, height: nh });

    let bw = C;
    let bh = C;

    if (nw > nh) {
      // Landscape
      bh = C;
      bw = C * (nw / nh);
    } else {
      // Portrait or Square
      bw = C;
      bh = C * (nh / nw);
    }

    setBaseSize({ width: bw, height: bh });
    // Center the image initially
    setOffset({
      x: (C - bw) / 2,
      y: (C - bh) / 2,
    });
    setImgLoaded(true);
  };

  // Render dimensions at current zoom
  const W = baseSize.width * zoom;
  const H = baseSize.height * zoom;

  // Clamp offset to ensure crop frame is fully filled by image
  const clampOffset = (x: number, y: number, currentZoom: number) => {
    const curW = baseSize.width * currentZoom;
    const curH = baseSize.height * currentZoom;
    const minX = C - curW;
    const minY = C - curH;

    return {
      x: curW <= C ? (C - curW) / 2 : Math.max(minX, Math.min(0, x)),
      y: curH <= C ? (C - curH) / 2 : Math.max(minY, Math.min(0, y)),
    };
  };

  // Pointer dragging handlers
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!imgLoaded) return;
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const nextX = e.clientX - dragStart.current.x;
    const nextY = e.clientY - dragStart.current.y;
    const clamped = clampOffset(nextX, nextY, zoom);
    setOffset(clamped);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Adjust zoom while keeping center of crop box focused
  const handleZoomChange = (nextZoom: number) => {
    const prevZoom = zoom;
    setZoom(nextZoom);

    // Zoom from center math:
    // C/2 - X_new = (C/2 - X_old) * (Z_new / Z_old)
    const newX = C / 2 - (C / 2 - offset.x) * (nextZoom / prevZoom);
    const newY = C / 2 - (C / 2 - offset.y) * (nextZoom / prevZoom);

    setOffset(clampOffset(newX, newY, nextZoom));
  };

  const handleCrop = () => {
    const img = imgRef.current;
    if (!img || !imgLoaded) return;

    const canvas = document.createElement('canvas');
    canvas.width = targetSize;
    canvas.height = targetSize;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Calculate source crop region on the original natural image
    const origX = (-offset.x / W) * naturalSize.width;
    const origY = (-offset.y / H) * naturalSize.height;
    const origWidth = (C / W) * naturalSize.width;
    const origHeight = (C / H) * naturalSize.height;

    // Draw to target size canvas
    ctx.drawImage(
      img,
      origX,
      origY,
      origWidth,
      origHeight,
      0,
      0,
      targetSize,
      targetSize
    );

    // Convert to compressed JPEG blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const croppedFile = new File([blob], fileName.replace(/\.[^/.]+$/, "") + '.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          onCrop(croppedFile);
        }
      },
      'image/jpeg',
      0.85 // 85% compression quality
    );
  };

  return (
    <div className="flex flex-col items-center space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
      <div className="text-center">
        <h4 className="text-sm font-semibold text-foreground">Crop your profile photo</h4>
        <p className="text-xs text-muted-foreground mt-0.5">Drag to reposition, slide to zoom</p>
      </div>

      {/* Viewport Frame */}
      <div
        className="relative overflow-hidden bg-slate-900 border border-slate-300 dark:border-slate-600 cursor-move touch-none"
        style={{ width: `${C}px`, height: `${C}px` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <img
          ref={imgRef}
          src={imageSrc}
          alt="Cropper Source"
          onLoad={handleImageLoad}
          className="absolute origin-top-left max-w-none pointer-events-none select-none"
          style={{
            width: `${W}px`,
            height: `${H}px`,
            left: `${offset.x}px`,
            top: `${offset.y}px`,
          }}
        />

        {/* Circular Mask Overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(circle, transparent ${C / 2 - 1}px, rgba(15, 23, 42, 0.65) ${C / 2}px)`,
          }}
        />
        {/* Guide ring */}
        <div
          className="absolute rounded-full border border-white/40 pointer-events-none"
          style={{
            width: `${C - 2}px`,
            height: `${C - 2}px`,
            left: '1px',
            top: '1px',
          }}
        />
      </div>

      {/* Controls */}
      <div className="w-full max-w-xs space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Zoom</span>
          <span>{Math.round(zoom * 100)}%</span>
        </div>
        <input
          type="range"
          min="1"
          max="3"
          step="0.01"
          value={zoom}
          onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
          className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-600 dark:bg-slate-700"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 rounded-md transition-colors dark:border-slate-600 dark:hover:bg-slate-700 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCrop}
          className="px-3 py-1.5 text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 rounded-md transition-colors cursor-pointer"
        >
          Apply Crop
        </button>
      </div>
    </div>
  );
}
